/**
 * User Management component for multi-tenant admin user management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAdmin } from '../../contexts/AdminContext';
import { UserSummary, UserFilters } from '../../types/admin';
import { formatDate, formatNumber } from '../../utils/formatters';

interface UserManagementProps {
  onNavigate?: (path: string) => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ onNavigate }) => {
  const {
    users,
    usersLoading,
    usersError,
    loadUsers,
    suspendUser,
    activateUser,
    clearError
  } = useAdmin();

  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    page_size: 20
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    type: 'suspend' | 'activate' | 'edit' | 'view';
    user: UserSummary | null;
  }>({
    open: false,
    type: 'view',
    user: null
  });

  useEffect(() => {
    loadUsers(filters);
  }, [loadUsers, filters]);

  const handleFilterChange = (key: keyof UserFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: UserSummary) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleAction = (type: 'suspend' | 'activate' | 'edit' | 'view') => {
    if (selectedUser) {
      setActionDialog({
        open: true,
        type,
        user: selectedUser
      });
    }
    handleMenuClose();
  };

  const handleActionConfirm = async () => {
    if (!actionDialog.user) return;

    try {
      switch (actionDialog.type) {
        case 'suspend':
          await suspendUser(actionDialog.user.id);
          break;
        case 'activate':
          await activateUser(actionDialog.user.id);
          break;
        case 'edit':
          // Handle edit - could open a form dialog
          console.log('Edit user:', actionDialog.user.id);
          break;
        case 'view':
          // Handle view - could navigate to user details
          console.log('View user:', actionDialog.user.id);
          break;
      }
    } catch (error) {
      console.error('Error performing action:', error);
    }

    setActionDialog({ open: false, type: 'view', user: null });
  };

  const handleActionCancel = () => {
    setActionDialog({ open: false, type: 'view', user: null });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSubscriptionTierColor = (tier: string) => {
    switch (tier) {
      case 'enterprise':
        return 'error';
      case 'pro':
        return 'warning';
      case 'standard':
        return 'info';
      case 'basic':
        return 'default';
      default:
        return 'default';
    }
  };

  if (usersLoading && !users) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (usersError) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={clearError}>
            Dismiss
          </Button>
        }
      >
        {usersError}
      </Alert>
    );
  }

  if (!users) {
    return (
      <Alert severity="info">
        No user data available. Please try refreshing.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => loadUsers(filters)} disabled={usersLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Search users"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Subscription</InputLabel>
              <Select
                value={filters.subscription_tier || ''}
                label="Subscription"
                onChange={(e) => handleFilterChange('subscription_tier', e.target.value || undefined)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="pro">Pro</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => loadUsers(filters)}
              disabled={usersLoading}
            >
              Apply Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Users ({formatNumber(users.total_count)})
            </Typography>
          </Box>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Webhooks</TableCell>
                  <TableCell>Alerts</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {user.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {user.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.subscription_tier}
                        color={getSubscriptionTierColor(user.subscription_tier) as any}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        color={getStatusColor(user.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatNumber(user.webhook_count)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatNumber(user.alert_count)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.last_login ? formatDate(user.last_login) : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, user)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {users.total_count > (filters.page_size || 20) && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(users.total_count / (filters.page_size || 20))}
                page={filters.page || 1}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleAction('view')}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction('edit')}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        {selectedUser?.status === 'active' ? (
          <MenuItem onClick={() => handleAction('suspend')}>
            <ListItemIcon>
              <BlockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Suspend User</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => handleAction('activate')}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Activate User</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialog.open} onClose={handleActionCancel}>
        <DialogTitle>
          {actionDialog.type === 'suspend' && 'Suspend User'}
          {actionDialog.type === 'activate' && 'Activate User'}
          {actionDialog.type === 'edit' && 'Edit User'}
          {actionDialog.type === 'view' && 'View User'}
        </DialogTitle>
        <DialogContent>
          {actionDialog.user && (
            <Box>
              <Typography variant="body1" mb={2}>
                {actionDialog.type === 'suspend' && 
                  `Are you sure you want to suspend user "${actionDialog.user.username}"?`
                }
                {actionDialog.type === 'activate' && 
                  `Are you sure you want to activate user "${actionDialog.user.username}"?`
                }
                {actionDialog.type === 'edit' && 
                  `Edit user "${actionDialog.user.username}"`
                }
                {actionDialog.type === 'view' && 
                  `View details for user "${actionDialog.user.username}"`
                }
              </Typography>
              {actionDialog.type === 'suspend' && (
                <Alert severity="warning">
                  Suspending a user will prevent them from accessing the system.
                </Alert>
              )}
              {actionDialog.type === 'activate' && (
                <Alert severity="info">
                  Activating a user will restore their access to the system.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActionCancel}>Cancel</Button>
          <Button 
            onClick={handleActionConfirm} 
            variant="contained"
            color={actionDialog.type === 'suspend' ? 'error' : 'primary'}
          >
            {actionDialog.type === 'suspend' && 'Suspend'}
            {actionDialog.type === 'activate' && 'Activate'}
            {actionDialog.type === 'edit' && 'Save'}
            {actionDialog.type === 'view' && 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
